import React, { Suspense } from "react";
import { Routes, Route, BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  ThemeProvider,
  createTheme,
} from "@material-ui/core/styles";
import { makeStyles } from "@material-ui/core/styles";
import NavBar from "components/layouts/NavBar";

import "../src/index.css";
// Redux
import { Provider as ReduxProvider } from "react-redux";
import store from "./store/store";
import Loader from "./components/UI/Loader";

import ProtectedRoute from "./components/common/ProtectedRoute";
import { routes } from "./routes";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Lazy load route components
const Login = React.lazy(() => import("components/layouts/Login"));
const ForgetPassword = React.lazy(() => import("components/layouts/ForgetPassword"));
const ForgetPasswordConfirm = React.lazy(() => import("./components/layouts/ForgetPasswordConfirm"));
const ChangePassword = React.lazy(() => import("./components/layouts/ChangePassword"));
const SignUp = React.lazy(() => import("components/layouts/SignUp"));
const Register = React.lazy(() => import("components/layouts/Register"));
const SignUpProfileUpload = React.lazy(() => import("./components/layouts/profileUploadPage"));
const Version = React.lazy(() => import("components/layouts/version"));
const NotFound = React.lazy(() => import("components/layouts/PageNotFound"));
const PermissionDenied = React.lazy(() => import("components/layouts/PermissionDenied"));
const Auth = React.lazy(() => import("components/common/Auth"));
const ErrorBoundry = React.lazy(() => import("components/common/ErrorBoundry"));

const defaultTheme = createTheme({
  typography: {
    subtitle1: {
      fontFamily: "Poppins",
      color: "#23AFC4",
    },
    subtitle2: {
      fontFamily: "Poppins",
      color: "#000000",
      fontWeight: 500,
      textAlign: "center",
    },
    h6: {
      fontFamily: "Poppins",
      fontWeight: 400,
      textAlign: "center",
      color: "#000000",
    },
    h3: {
      fontFamily: "Poppins",
      fontWeight: 600,
      textAlign: "center",
      color: "#000000",
    },
    h5: {
      fontFamily: "Poppins",
      fontWeight: 500,
      textAlign: "center",
      color: "#444444;",
    },
    h4: {
      fontFamily: "Poppins",
      fontWeight: 400,
      textAlign: "center",
      color: "#444444;",
    },
    h2: {
      fontFamily: "Poppins",
      fontWeight: 400,
      textAlign: "center",
      color: "#777777;",
    },
  },
  palette: {
    type: "light",
    action: {
      disabledBackground: "#0F9BB0",
    },
    primary: {
      main: "#0F9BB0",
      fontFamily: "Poppins",
    },
    secondary: {
      main: "#23AFC4",
    },
  },
  overrides: {
    MuiFormLabel: {
      asterisk: {
        color: "#db3131",
        "&$error": {
          color: "#db3131",
        },
      },
    },
    MuiTextField: {
      root: {
        "& label.Mui-focused": {
          color: "#0F9BB0",
        },
        "& .MuiInput-underline:after": {
          borderBottomColor: "#0F9BB0",
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: "#0F9BB0",
          },
          "&:hover fieldset": {
            borderColor: "#0F9BB0",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#0F9BB0",
          },
        },
        "& .MuiFormLabel-root.Mui-error": {
          color: "gray !important",
        },
        "& .MuiFormHelperText-root.Mui-error": {
          color: "red !important",
        },
        "& .MuiInputBase-input.Mui-disabled": {
          WebkitTextFillColor: "black",
          color: "black",
        },
      },
    },
  },
});

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
  },
  self: {
    backgroundColor: "blue",
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
  },
}));

function App(props) {
  const classes = useStyles();

  return (
    <ErrorBoundry>
      <ReduxProvider store={store}>
        <ThemeProvider theme={defaultTheme}>
          <div className={classes.root}>
            <BrowserRouter>
              <NavBar {...props}> </NavBar>

              <Suspense
                fallback={
                  <div>
                    <Loader />
                  </div>
                }
              >
                <Auth>
                 
                    <SentryRoutes>
                      <Route path="/" element={<Login />} />
                      <Route path="/version" element={<Version />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/forgetPassword" element={<ForgetPassword />} />
                      <Route path="/reset_password" element={<ChangePassword />} />
                      <Route
                        path="/forgetPasswordConfirm"
                        element={<ForgetPasswordConfirm />}
                      />
                      <Route path="/signUp" element={<SignUp />} />
                      <Route path="/profile" element={<SignUpProfileUpload />} />
                      <Route path="/register/" element={<Register />} />
                      <Route
                        path="/permission-denied"
                        element={<PermissionDenied />}
                      />
                      {routes.map(({ element, path, name }) => (
                          <Route
                            key={name}
                            path={path}
                            element={<ProtectedRoute element={element} />}
                          />
                        ))}
                         <Route path="*" element={<NotFound />} />
                      
                    </SentryRoutes>
                   
                  
                   
                </Auth>
              </Suspense>
            </BrowserRouter>
          </div>
        </ThemeProvider>
      </ReduxProvider>
    </ErrorBoundry>
  );
}

export default App;
